/**
 * Shared utilities for React component code generation.
 */

import transformData from '../transformData';
import { deploymentProfile } from '../../config/deploymentProfile';
import { LEGEND_SPACING, TITLE_SPACING } from '../chartLayout';

export { LEGEND_SPACING, TITLE_SPACING };

export const J = JSON.stringify;
export const JN = (v) => JSON.stringify(v, null, 2);

// ─── Data resolution ─────────────────────────────────────────────────────────

export function computeData(chartType, config, isLoaded, rawData, mapping) {
  if (isLoaded && rawData.length > 0 && Object.keys(mapping).length > 0) {
    const transformed = transformData(rawData, mapping, chartType.id);
    if (transformed && transformed.length > 0) return transformed;
  }
  if (chartType.getSampleData) return chartType.getSampleData(config);
  return chartType.sampleData || [];
}

// ─── Theme snapshot (only chart-relevant tokens) ─────────────────────────────

export function themeSnap(theme) {
  const withFallbackStack = (font) => {
    const fallback = deploymentProfile.font.reactFallbackStack;
    if (!font || !String(font).trim()) return fallback;
    const value = String(font).trim();
    if (/(sans-serif|serif|monospace|system-ui)/i.test(value)) return value;
    return `${value}, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif`;
  };
  return {
    background: theme.background || '#FFFFFF',
    foreground: theme.foreground || '#000000',
    axisColor: theme.axisColor || '#000000',
    gridColor: theme.gridColor || '#CCCCCC',
    gridAlpha: theme.gridAlpha ?? 0.3,
    tickColor: theme.tickColor || '#000000',
    labelColor: theme.labelColor || '#000000',
    titleColor: theme.titleColor || '#000000',
    legendTextColor: theme.legendTextColor || '#000000',
    referenceLineColor: theme.referenceLineColor || '#C44B3D',
    barRadius: theme.barRadius ?? 4,
    spineWidth: theme.spineWidth ?? 1,
    fontBody: withFallbackStack(theme.fontBody),
    fontHeading: withFallbackStack(theme.fontHeading || theme.fontBody),
    fontLabel: withFallbackStack(theme.fontLabel || theme.fontHeading || theme.fontBody),
    fontNumber: withFallbackStack(theme.fontNumber || theme.fontBody),
    palette: theme.palette,
  };
}

// ─── Helper: file header comment ─────────────────────────────────────────────

export function fileHeader(packages, chartName) {
  const pkgLine = packages.join(' ');
  return `/*
 * Required npm packages:
 *   npm install ${pkgLine}
 *
 * Usage:
 *   import Chart from './${chartName}.jsx';
 *   <Chart width={720} height={480} />
 */
`;
}
